import { useLazyQuery, useQuery } from '@apollo/client/react';
import { uniq } from 'lodash-es';
import pDebounce from 'p-debounce';
import { useEffect } from 'react';

import type { Option } from '@zen/DesignSystem/types';
import type { Optional } from '@zen/utils/typescript';

import { AllAccountsDocument, type AllAccountsQueryResult, type AllAccountsQueryVariables } from '../graphql';
import type { Account } from '../graphql/types';
import { AccountStatus, type OrganizationalRoleEnum } from '../types';

type SearchableAccountOptionsArguments = {
  assignableOnly?: boolean;
  limitAccounts?: string[];
  nestedBusinessUnits?: boolean;
  organizationalRole: OrganizationalRoleEnum[];
  // TODO - IMPLEMENT QUERY FILTER TO TAKE ACCOUNT_IDS
  selectedValues?: string[];
  status?: AccountStatus[];
};

export const useSearchableAccountOptions = (args: SearchableAccountOptionsArguments) => {
  const {
    assignableOnly = false,
    limitAccounts,
    organizationalRole,
    selectedValues,
    status = [AccountStatus.ACTIVE],
    nestedBusinessUnits = false
  } = args;

  // Merge limitAccounts and selectedValues and deduplicate
  const mergedAccountIds: string[] | undefined =
    limitAccounts || selectedValues ? uniq([...(limitAccounts || []), ...(selectedValues || [])]) : undefined;

  const [fetchAccounts, { data, loading, error }] = useLazyQuery<AllAccountsQueryResult, AllAccountsQueryVariables>(
    AllAccountsDocument,
    { fetchPolicy: 'no-cache' }
  );
  const { data: accountsSelected } = useQuery<AllAccountsQueryResult, AllAccountsQueryVariables>(AllAccountsDocument, {
    variables: {
      accountsFiltersInput: {
        assignableOnly,
        accountIds: mergedAccountIds,
        organizationalRole,
        status
      },
      nestedBusinessUnits
    },
    fetchPolicy: 'no-cache',
    skip: !mergedAccountIds
  });

  useEffect(() => {
    if (mergedAccountIds) {
      fetchAccounts({
        variables: {
          accountsFiltersInput: {
            assignableOnly,
            accountIds: mergedAccountIds,
            organizationalRole,
            status
          },
          nestedBusinessUnits
        }
      });
    } else {
      fetchAccounts({
        variables: {
          accountsFiltersInput: {
            assignableOnly,
            organizationalRole,
            searchQuery: '',
            status
          },
          nestedBusinessUnits
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleInputChange = (query: string): void => {
    if (query) {
      fetchAccounts({
        variables: {
          accountsFiltersInput: {
            accountIds: undefined,
            assignableOnly,
            organizationalRole,
            status,
            searchQuery: query
          },
          nestedBusinessUnits
        }
      });
    }
  };

  const debounceDelay: number = 300;
  const debouncedHandleInputChange = pDebounce(handleInputChange, debounceDelay);

  const createAccountOption = (account: Optional<Account>): Option<string> => ({
    label: account?.label?.long || '',
    value: account?.id || ''
  });

  const selectedOptions: Option<string>[] = accountsSelected?.accounts?.nodes?.map(createAccountOption) || [];

  const options: Option<string>[] = data?.accounts?.nodes?.map(createAccountOption) || [];

  return {
    options,
    loading,
    error,
    onSearch: debouncedHandleInputChange,
    selectedOptions
  };
};
